import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Layout/Header';
import { payrollEngineAPI, departmentAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Plus, Calculator, CheckCircle, XCircle, FileSpreadsheet, 
  Users, Wallet, RefreshCw, Eye, Play, Download, Settings,
  DollarSign, Percent, Building, FileText, Clock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { HelpTooltip } from '../components/HelpTooltip';

const PayrollEngine = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('runs');
  const [loading, setLoading] = useState(false);
  const [downloadingSlipId, setDownloadingSlipId] = useState(null); // Per-slip loading state
  
  // Payroll Runs
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [slips, setSlips] = useState([]);
  const [slipsPage, setSlipsPage] = useState(1);
  const [slipsTotal, setSlipsTotal] = useState(0);
  
  // Create Run Modal
  const [createRunOpen, setCreateRunOpen] = useState(false);
  const [newRunName, setNewRunName] = useState('');
  const [newRunMonth, setNewRunMonth] = useState(new Date().getMonth() + 1);
  const [newRunYear, setNewRunYear] = useState(new Date().getFullYear());
  const [newRunDepartment, setNewRunDepartment] = useState('');
  const [departments, setDepartments] = useState([]);
  
  // Components
  const [components, setComponents] = useState([]);
  const [componentModalOpen, setComponentModalOpen] = useState(false);
  const [componentForm, setComponentForm] = useState({
    code: '', name: '', type: 'earning', calculation_type: 'fixed',
    fixed_amount: 0, percentage: 0, base_component: 'BASIC',
    is_taxable: true, is_statutory: false
  });
  
  // Statutory Rates
  const [statutoryRates, setStatutoryRates] = useState([]);
  
  // Tax Slabs
  const [taxSlabs, setTaxSlabs] = useState([]);
  
  // Reports
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [deptWise, setDeptWise] = useState([]);

  const isAdmin = user?.role === 'admin';
  const isHR = user?.role === 'hr';
  const canManage = isAdmin || isHR;

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
  ];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  const fetchRuns = useCallback(async () => {
    try {
      const response = await payrollEngineAPI.getRuns({ year: reportYear });
      setRuns(response.data || []);
    } catch (error) {
      console.error('Error fetching runs:', error);
    }
  }, [reportYear]);

  const fetchComponents = useCallback(async () => {
    try {
      const response = await payrollEngineAPI.getComponents();
      setComponents(response.data || []);
    } catch (error) {
      console.error('Error fetching components:', error);
    }
  }, []);

  const fetchStatutoryRates = useCallback(async () => {
    try {
      const response = await payrollEngineAPI.getStatutoryRates();
      setStatutoryRates(response.data || []);
    } catch (error) {
      console.error('Error fetching statutory rates:', error);
    }
  }, []);

  const fetchTaxSlabs = useCallback(async () => {
    try {
      const response = await payrollEngineAPI.getTaxSlabs();
      setTaxSlabs(response.data || []);
    } catch (error) {
      console.error('Error fetching tax slabs:', error);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await departmentAPI.getAll();
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
    fetchDepartments();
  }, [fetchRuns, fetchDepartments]);

  useEffect(() => {
    if (activeTab === 'components') fetchComponents();
    if (activeTab === 'statutory') fetchStatutoryRates();
    if (activeTab === 'tax') fetchTaxSlabs();
  }, [activeTab, fetchComponents, fetchStatutoryRates, fetchTaxSlabs]);

  const fetchSlips = async (runId, page = 1) => {
    try {
      setLoading(true);
      const response = await payrollEngineAPI.getSlips(runId, { page, limit: 50 });
      setSlips(response.data.data || []);
      setSlipsTotal(response.data.total || 0);
      setSlipsPage(page);
    } catch (error) {
      toast.error('Failed to fetch salary slips');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRun = async () => {
    if (!newRunName.trim()) {
      toast.error('Please enter a run name');
      return;
    }
    try {
      setLoading(true);
      await payrollEngineAPI.createRun(newRunName, newRunMonth, newRunYear, newRunDepartment || null);
      toast.success('Payroll run created successfully');
      setCreateRunOpen(false);
      setNewRunName('');
      fetchRuns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payroll run');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async (runId) => {
    try {
      setLoading(true);
      const response = await payrollEngineAPI.calculatePayroll(runId);
      toast.success(response.data.message || 'Payroll calculated successfully');
      fetchRuns();
      if (selectedRun?.id === runId) {
        fetchSlips(runId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (runId) => {
    try {
      setLoading(true);
      await payrollEngineAPI.verifyPayroll(runId);
      toast.success('Payroll verified successfully');
      fetchRuns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (runId) => {
    try {
      setLoading(true);
      await payrollEngineAPI.approvePayroll(runId);
      toast.success('Payroll approved successfully');
      fetchRuns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBankFile = async (runId, format = 'neft') => {
    try {
      setLoading(true);
      const response = await payrollEngineAPI.generateBankFile(runId, format);
      toast.success(`Bank file generated: ${response.data.total_records} records, Total: ${formatCurrency(response.data.total_amount)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate bank file');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSlipPdf = async (slip) => {
    try {
      setDownloadingSlipId(slip.id);
      const response = await payrollEngineAPI.downloadSlipPdf(slip.id);
      
      // Create blob from response and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[slip.month] || 'Unknown';
      const empCode = slip.employee_code || 'EMP';
      link.download = `Payslip_${empCode}_${monthName}_${slip.year}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Payslip PDF downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download payslip PDF');
    } finally {
      setDownloadingSlipId(null);
    }
  };

  const handleCreateComponent = async () => {
    if (!componentForm.code || !componentForm.name) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      setLoading(true);
      await payrollEngineAPI.createComponent(componentForm);
      toast.success('Component created successfully');
      setComponentModalOpen(false);
      setComponentForm({
        code: '', name: '', type: 'earning', calculation_type: 'fixed',
        fixed_amount: 0, percentage: 0, base_component: 'BASIC',
        is_taxable: true, is_statutory: false
      });
      fetchComponents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create component');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [summaryRes, deptRes] = await Promise.all([
        payrollEngineAPI.getSummary(reportMonth, reportYear),
        payrollEngineAPI.getDepartmentWise(reportMonth, reportYear)
      ]);
      setSummary(summaryRes.data);
      setDeptWise(deptRes.data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-slate-100 text-slate-700', label: 'Draft' },
      calculated: { color: 'bg-blue-100 text-blue-700', label: 'Calculated' },
      verified: { color: 'bg-amber-100 text-amber-700', label: 'Verified' },
      approved: { color: 'bg-green-100 text-green-700', label: 'Approved' },
      paid: { color: 'bg-emerald-100 text-emerald-700', label: 'Paid' },
      cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancelled' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Advanced Payroll Engine</h1>
              <p className="text-slate-600">Comprehensive payroll processing with tax calculations</p>
            </div>
            <HelpTooltip moduleId="payroll-engine" />
          </div>
          {canManage && (
            <Button onClick={() => setCreateRunOpen(true)} className="gap-2" data-testid="create-run-btn">
              <Plus className="w-4 h-4" /> New Payroll Run
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="runs" className="gap-2">
              <Calculator className="w-4 h-4" /> Payroll Runs
            </TabsTrigger>
            <TabsTrigger value="components" className="gap-2">
              <DollarSign className="w-4 h-4" /> Pay Components
            </TabsTrigger>
            <TabsTrigger value="statutory" className="gap-2">
              <Percent className="w-4 h-4" /> Statutory Rates
            </TabsTrigger>
            <TabsTrigger value="tax" className="gap-2">
              <FileText className="w-4 h-4" /> Tax Slabs
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2">
              <FileSpreadsheet className="w-4 h-4" /> Reports
            </TabsTrigger>
          </TabsList>

          {/* Payroll Runs Tab */}
          <TabsContent value="runs">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Runs List */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5" /> Payroll Runs
                    </CardTitle>
                    <div className="flex gap-2 mt-2">
                      <Select value={String(reportYear)} onValueChange={(v) => setReportYear(Number(v))}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" onClick={fetchRuns}>
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                    {runs.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No payroll runs found</p>
                    ) : (
                      runs.map(run => (
                        <div
                          key={run.id}
                          onClick={() => { setSelectedRun(run); fetchSlips(run.id); }}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedRun?.id === run.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'
                          }`}
                          data-testid={`run-${run.id}`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-sm">{run.name}</p>
                              <p className="text-xs text-slate-500">
                                {months.find(m => m.value === run.month)?.label} {run.year}
                              </p>
                            </div>
                            {getStatusBadge(run.status)}
                          </div>
                          <div className="mt-2 flex gap-4 text-xs text-slate-600">
                            <span><Users className="w-3 h-3 inline mr-1" />{run.total_employees}</span>
                            <span><Wallet className="w-3 h-3 inline mr-1" />{formatCurrency(run.total_net)}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Run Details */}
              <div className="lg:col-span-2">
                {selectedRun ? (
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle>{selectedRun.name}</CardTitle>
                          <CardDescription>
                            {months.find(m => m.value === selectedRun.month)?.label} {selectedRun.year}
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          {selectedRun.status === 'draft' && canManage && (
                            <Button size="sm" onClick={() => handleCalculate(selectedRun.id)} disabled={loading}>
                              <Play className="w-4 h-4 mr-1" /> Calculate
                            </Button>
                          )}
                          {selectedRun.status === 'calculated' && canManage && (
                            <Button size="sm" variant="outline" onClick={() => handleVerify(selectedRun.id)} disabled={loading}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Verify
                            </Button>
                          )}
                          {selectedRun.status === 'verified' && isAdmin && (
                            <Button size="sm" className="bg-green-600" onClick={() => handleApprove(selectedRun.id)} disabled={loading}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                          )}
                          {selectedRun.status === 'approved' && canManage && (
                            <Button size="sm" variant="outline" onClick={() => handleGenerateBankFile(selectedRun.id)} disabled={loading}>
                              <Download className="w-4 h-4 mr-1" /> Bank File
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-4 gap-4 mb-4">
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600">Employees</p>
                          <p className="text-lg font-bold text-blue-700">{selectedRun.total_employees}</p>
                        </div>
                        <div className="p-3 bg-emerald-50 rounded-lg">
                          <p className="text-xs text-emerald-600">Gross</p>
                          <p className="text-lg font-bold text-emerald-700">{formatCurrency(selectedRun.total_gross)}</p>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg">
                          <p className="text-xs text-amber-600">Deductions</p>
                          <p className="text-lg font-bold text-amber-700">{formatCurrency(selectedRun.total_deductions)}</p>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-xs text-purple-600">Net Payable</p>
                          <p className="text-lg font-bold text-purple-700">{formatCurrency(selectedRun.total_net)}</p>
                        </div>
                      </div>

                      {/* Salary Slips Table */}
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Employee</TableHead>
                              <TableHead className="text-right">Gross</TableHead>
                              <TableHead className="text-right">Deductions</TableHead>
                              <TableHead className="text-right">Net</TableHead>
                              <TableHead>Days</TableHead>
                              <TableHead className="text-center">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {slips.map(slip => (
                              <TableRow key={slip.id}>
                                <TableCell>
                                  <p className="font-medium">{slip.employee_name || slip.employee_code}</p>
                                  <p className="text-xs text-slate-500">{slip.employee_code}</p>
                                </TableCell>
                                <TableCell className="text-right">{formatCurrency(slip.gross_earnings)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(slip.total_deductions)}</TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(slip.net_salary)}</TableCell>
                                <TableCell>
                                  <span className="text-xs">{slip.paid_days}/{slip.total_days}</span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleDownloadSlipPdf(slip)}
                                    disabled={downloadingSlipId === slip.id}
                                    data-testid={`download-pdf-${slip.id}`}
                                    className="gap-1"
                                  >
                                    {downloadingSlipId === slip.id ? (
                                      <>
                                        <span className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                                        <span>...</span>
                                      </>
                                    ) : (
                                      <>
                                        <FileText className="w-3 h-3" />
                                        PDF
                                      </>
                                    )}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {slips.length === 0 && (
                          <p className="text-center py-4 text-slate-500">
                            {selectedRun.status === 'draft' ? 'Click Calculate to process payroll' : 'No salary slips found'}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="flex items-center justify-center h-full min-h-[400px]">
                    <div className="text-center text-slate-500">
                      <Calculator className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Select a payroll run to view details</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Pay Components Tab */}
          <TabsContent value="components">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Pay Components</CardTitle>
                  <CardDescription>Configure salary components (earnings and deductions)</CardDescription>
                </div>
                {canManage && (
                  <Button onClick={() => setComponentModalOpen(true)} size="sm">
                    <Plus className="w-4 h-4 mr-1" /> Add Component
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Earnings */}
                  <div>
                    <h3 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" /> Earnings
                    </h3>
                    <div className="space-y-2">
                      {components.filter(c => c.type === 'earning').map(comp => (
                        <div key={comp.id} className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex justify-between">
                            <span className="font-medium">{comp.code}</span>
                            <Badge variant="outline">{comp.calculation_type}</Badge>
                          </div>
                          <p className="text-sm text-slate-600">{comp.name}</p>
                          {comp.calculation_type === 'fixed' && (
                            <p className="text-xs text-slate-500">Fixed: {formatCurrency(comp.fixed_amount)}</p>
                          )}
                          {comp.calculation_type === 'percentage' && (
                            <p className="text-xs text-slate-500">{comp.percentage}% of {comp.base_component}</p>
                          )}
                        </div>
                      ))}
                      {components.filter(c => c.type === 'earning').length === 0 && (
                        <p className="text-slate-500 text-sm">No earning components configured</p>
                      )}
                    </div>
                  </div>
                  {/* Deductions */}
                  <div>
                    <h3 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                      <Percent className="w-4 h-4" /> Deductions
                    </h3>
                    <div className="space-y-2">
                      {components.filter(c => c.type === 'deduction').map(comp => (
                        <div key={comp.id} className="p-3 bg-red-50 rounded-lg border border-red-200">
                          <div className="flex justify-between">
                            <span className="font-medium">{comp.code}</span>
                            <Badge variant="outline">{comp.calculation_type}</Badge>
                          </div>
                          <p className="text-sm text-slate-600">{comp.name}</p>
                          {comp.is_statutory && <Badge className="mt-1 bg-blue-100 text-blue-700">Statutory</Badge>}
                        </div>
                      ))}
                      {components.filter(c => c.type === 'deduction').length === 0 && (
                        <p className="text-slate-500 text-sm">No deduction components configured</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Statutory Rates Tab */}
          <TabsContent value="statutory">
            <Card>
              <CardHeader>
                <CardTitle>Statutory Deduction Rates</CardTitle>
                <CardDescription>EPF, ESI, Professional Tax, and other statutory rates</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Employee Rate</TableHead>
                      <TableHead>Employer Rate</TableHead>
                      <TableHead>Wage Ceiling</TableHead>
                      <TableHead>Effective From</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statutoryRates.map(rate => (
                      <TableRow key={rate.id}>
                        <TableCell className="font-medium uppercase">{rate.type}</TableCell>
                        <TableCell>{rate.employee_rate}%</TableCell>
                        <TableCell>{rate.employer_rate}%</TableCell>
                        <TableCell>{rate.wage_ceiling ? formatCurrency(rate.wage_ceiling) : 'No limit'}</TableCell>
                        <TableCell>{rate.effective_from}</TableCell>
                        <TableCell>
                          {rate.is_active ? (
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-700">Inactive</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {statutoryRates.length === 0 && (
                  <p className="text-center py-4 text-slate-500">No statutory rates configured</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tax Slabs Tab */}
          <TabsContent value="tax">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* New Regime */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" /> New Tax Regime
                  </CardTitle>
                  <CardDescription>FY 2024-25 tax slabs under new regime</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Income Range</TableHead>
                        <TableHead>Tax Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {taxSlabs.filter(s => s.regime === 'new').map(slab => (
                        <TableRow key={slab.id}>
                          <TableCell>
                            {formatCurrency(slab.min_income)} - {slab.max_income === 0 ? 'Above' : formatCurrency(slab.max_income)}
                          </TableCell>
                          <TableCell>{slab.tax_rate}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              {/* Old Regime */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" /> Old Tax Regime
                  </CardTitle>
                  <CardDescription>FY 2024-25 tax slabs under old regime</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Income Range</TableHead>
                        <TableHead>Tax Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {taxSlabs.filter(s => s.regime === 'old').map(slab => (
                        <TableRow key={slab.id}>
                          <TableCell>
                            {formatCurrency(slab.min_income)} - {slab.max_income === 0 ? 'Above' : formatCurrency(slab.max_income)}
                          </TableCell>
                          <TableCell>{slab.tax_rate}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Payroll Reports</CardTitle>
                      <CardDescription>Monthly payroll summary and department-wise breakdown</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Select value={String(reportMonth)} onValueChange={(v) => setReportMonth(Number(v))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={String(reportYear)} onValueChange={(v) => setReportYear(Number(v))}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button onClick={fetchReports} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {summary ? (
                    <div className="grid grid-cols-5 gap-4 mb-6">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-700">{summary.total_employees}</p>
                        <p className="text-sm text-blue-600">Employees</p>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-700">{formatCurrency(summary.total_gross)}</p>
                        <p className="text-sm text-emerald-600">Total Gross</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-700">{formatCurrency(summary.total_deductions)}</p>
                        <p className="text-sm text-amber-600">Deductions</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-700">{formatCurrency(summary.total_net)}</p>
                        <p className="text-sm text-purple-600">Net Payable</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-slate-700">{formatCurrency(summary.avg_net || 0)}</p>
                        <p className="text-sm text-slate-600">Avg. Net</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-center py-4 text-slate-500">Select month and year, then click refresh to load reports</p>
                  )}

                  {/* Department-wise breakdown */}
                  {deptWise.length > 0 && (
                    <div>
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <Building className="w-4 h-4" /> Department-wise Breakdown
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Department</TableHead>
                            <TableHead className="text-right">Employees</TableHead>
                            <TableHead className="text-right">Gross</TableHead>
                            <TableHead className="text-right">Deductions</TableHead>
                            <TableHead className="text-right">Net</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {deptWise.map(dept => (
                            <TableRow key={dept.department_id}>
                              <TableCell className="font-medium">{dept.department_name}</TableCell>
                              <TableCell className="text-right">{dept.employee_count}</TableCell>
                              <TableCell className="text-right">{formatCurrency(dept.total_gross)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(dept.total_deductions)}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(dept.total_net)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Payroll Run Modal */}
        <Dialog open={createRunOpen} onOpenChange={setCreateRunOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Payroll Run</DialogTitle>
              <DialogDescription>Start a new payroll processing cycle</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Run Name</label>
                <Input
                  value={newRunName}
                  onChange={(e) => setNewRunName(e.target.value)}
                  placeholder="e.g., October 2024 Payroll"
                  data-testid="run-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Month</label>
                  <Select value={String(newRunMonth)} onValueChange={(v) => setNewRunMonth(Number(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Year</label>
                  <Select value={String(newRunYear)} onValueChange={(v) => setNewRunYear(Number(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Department (Optional)</label>
                <Select value={newRunDepartment || '__all__'} onValueChange={(v) => setNewRunDepartment(v === '__all__' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Departments</SelectItem>
                    {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateRunOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateRun} disabled={loading} data-testid="create-run-submit">
                {loading ? 'Creating...' : 'Create Run'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Component Modal */}
        <Dialog open={componentModalOpen} onOpenChange={setComponentModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Pay Component</DialogTitle>
              <DialogDescription>Configure a new salary component</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Code</label>
                  <Input
                    value={componentForm.code}
                    onChange={(e) => setComponentForm({...componentForm, code: e.target.value.toUpperCase()})}
                    placeholder="e.g., HRA"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    value={componentForm.name}
                    onChange={(e) => setComponentForm({...componentForm, name: e.target.value})}
                    placeholder="e.g., House Rent Allowance"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <Select value={componentForm.type} onValueChange={(v) => setComponentForm({...componentForm, type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="earning">Earning</SelectItem>
                      <SelectItem value="deduction">Deduction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Calculation</label>
                  <Select value={componentForm.calculation_type} onValueChange={(v) => setComponentForm({...componentForm, calculation_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="formula">Formula</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {componentForm.calculation_type === 'fixed' && (
                <div>
                  <label className="text-sm font-medium">Fixed Amount</label>
                  <Input
                    type="number"
                    value={componentForm.fixed_amount}
                    onChange={(e) => setComponentForm({...componentForm, fixed_amount: Number(e.target.value)})}
                  />
                </div>
              )}
              {componentForm.calculation_type === 'percentage' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Percentage</label>
                    <Input
                      type="number"
                      value={componentForm.percentage}
                      onChange={(e) => setComponentForm({...componentForm, percentage: Number(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Base Component</label>
                    <Input
                      value={componentForm.base_component}
                      onChange={(e) => setComponentForm({...componentForm, base_component: e.target.value})}
                      placeholder="BASIC"
                    />
                  </div>
                </div>
              )}
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={componentForm.is_taxable}
                    onChange={(e) => setComponentForm({...componentForm, is_taxable: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Taxable</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={componentForm.is_statutory}
                    onChange={(e) => setComponentForm({...componentForm, is_statutory: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Statutory</span>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setComponentModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateComponent} disabled={loading}>
                {loading ? 'Creating...' : 'Create Component'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default PayrollEngine;
